import React, { useState, useEffect } from 'react';
import { Typography } from '@/lib/typography';
import { useParams, useNavigate } from 'react-router-dom';
import { PageShell } from '@/components/shared/PageShell';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import FounderEditForm from '../../../components/crm/founders/FounderEditForm';
import { getFounder, updateFounder } from '@/lib/api';
import { ArrowLeft } from '@phosphor-icons/react';

import { adaptFounder } from '@/adapters/founder.adapter';
import { FounderRenderContract } from '@/contracts/render_contracts';
import { RuntimeContractValidator } from '@/validators/runtime_validator';
import { ROUTES } from '@/config/product-contract';

// Page state labels — noscan: functional status text, not shared business labels
const PAGE_STATES = {
    loadingTitle: 'Loading', fetchingDesc: 'Fetching details', // noscan
    errorTitle: 'Error', failedDesc: 'Failed to load', // noscan
    notFoundTitle: 'Not Found', invalidDesc: 'Invalid ID', // noscan
};

export default function FounderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [founder, setFounder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (id) {
      fetchFounder();
    }
  }, [id]);

  const fetchFounder = async () => {
    setLoading(true);
    try {
      const data = await getFounder(id);

      // Truth Validation (Phase 3)
      // FIX: Validate ADAPTED data against Render Contract
      const adaptedFounder = adaptFounder(data);
      RuntimeContractValidator.validateEntry(adaptedFounder, FounderRenderContract, 'Founder Detail');

      setFounder(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (formData) => {
    setSaving(true);
    try {
      await updateFounder(id, formData);
      await fetchFounder();
      setIsEditing(false);
    } catch (err) {
      alert('Failed to update: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <PageShell title={PAGE_STATES.loadingTitle} description={PAGE_STATES.fetchingDesc}><div className="p-6 text-center">Loading...</div></PageShell>;
  if (error) return <PageShell title={PAGE_STATES.errorTitle} description={PAGE_STATES.failedDesc}><div className="p-6 text-red-600 text-center">{error}</div></PageShell>;
  if (!founder) return <PageShell title={PAGE_STATES.notFoundTitle} description={PAGE_STATES.invalidDesc}><div className="p-6 text-center">Founder not found</div></PageShell>;

  // Identity: Component must never touch raw API data
  const adaptedFounder = adaptFounder(founder);

  return (
    <PageShell
      title={adaptedFounder.display_name}
      description={adaptedFounder.subtitle}
      actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate(ROUTES.FOUNDERS)}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Founders
          </Button>
          {!isEditing && (
            <Button onClick={() => setIsEditing(true)}>
              Edit Founder
            </Button>
          )}
        </div>
      }
    >
      {isEditing ? (
        <FounderEditForm
          founder={founder}
          onSave={handleSave}
          onCancel={() => setIsEditing(false)}
          saving={saving}
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Basic Info Card */}
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label>Full Name</Label>
                  <p >{adaptedFounder.display_name}</p>
                </div>
                <div>
                  <Label>Email</Label>
                  <p >{adaptedFounder.email}</p>
                </div>
                <div>
                  <Label>Startup</Label>
                  <p >{adaptedFounder.subtitle}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Info Card */}
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label>Contact Status</Label>
                  <p >{adaptedFounder.contact_status}</p>
                </div>
                <div>
                  <Label>Email Status</Label>
                  <p >{adaptedFounder.email_status}</p>
                </div>
                <div>
                  <Label>Last Contact</Label>
                  <p >
                    {adaptedFounder.last_contact_label}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </PageShell>
  );
}
